import React, { useState, useEffect } from 'react';
import './ChatInterface.css';
import AIChat from './AIChat';
import GroupChat from './GroupChat';
import Header from '../../components/Header';
import { userService, chatService, conversationsService, messagesService } from '../../services';
import { getApiBaseUrl } from '../../config/apiConfig';
import AddMemberModal from '../../components/AddMemberModal';
import { signalRService } from '../../services/signalRService';

export default function ChatInterface({ onClose, fullPage = false }) {
  const [selectedChat, setSelectedChat] = useState(null);
  const [aiConversationId, setAiConversationId] = useState(() => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
      const userId = currentUser?.id ? String(currentUser.id) : null;
      // Only restore a per-user stored AI conversation id. Do NOT restore a global key for anonymous users.
      return userId ? (localStorage.getItem(`aiConversationId_${userId}`) || null) : null;
    } catch { return null; }
  });
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [showChatList, setShowChatList] = useState(true);
  const [chats, setChats] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);

  const defaultChatOptions = [
    { id: 'group', name: 'Nhóm', avatar: '👥', status: '3 thành viên' },
    { id: 'ai', name: 'Trò chuyện AI', avatar: '🤖', status: 'Trực tuyến' }
  ];

  // Load conversations from backend
  const loadConversations = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
      if (!currentUser?.id) {
        setChats(defaultChatOptions);
        return;
      }

      // Get user's conversations
      const userConversations = await conversationsService.getByUser(currentUser.id);
      const conversationList = Array.isArray(userConversations) ? userConversations : [];

      // Try to discover the global AI conversation and load it so it appears in the left list
      let aiConversationItem = null;
      try {
        const aiInfoRes = await fetch(`${getApiBaseUrl()}/ai/info`);
        if (aiInfoRes.ok) {
          const info = await aiInfoRes.json();
          const aiConvId = info?.DiscoveredAiConversationId || info?.ConfiguredConversationId || null;
          if (aiConvId) {
            try {
              const aiConv = await conversationsService.getById(aiConvId);
                if (aiConv) {
                aiConversationItem = {
                  id: aiConv.id || aiConv.conversationId || aiConv._id || 'ai',
                  name: 'Trò chuyện AI',
                  avatar: '🤖',
                  status: 'Trực tuyến',
                  conversation: aiConv
                };
                // remember discovered ai conversation id
                try {
                  const convIdVal = aiConv.id || aiConv.conversationId || aiConv._id || null;
                  setAiConversationId(convIdVal);
                  // persist only for authenticated users. Do NOT persist a global aiConversationId for anonymous users.
                  try {
                    const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
                    const userId = currentUser?.id ? String(currentUser.id) : null;
                    if (userId) localStorage.setItem(`aiConversationId_${userId}`, convIdVal);
                  } catch {}
                } catch {}
              }
            } catch (err) {
              console.warn('Failed to load AI conversation by id', aiConvId, err);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to fetch /api/ai/info', err);
      }

      // Map conversations to chat format
      const mappedChats = conversationList.map(conv => ({
        id: conv.id || conv.conversationId || conv._id,
        name: conv.name || conv.title || 'Cuộc trò chuyện',
         avatar: conv.avatarUrl || conv.avatar_url || conv.image || conv.avatar || '👤',
        status: conv.status || 'Hoạt động',
        conversation: conv
      }));

      // Insert AI conversation at the top if discovered, else include default AI option
      const finalChats = [];
      if (aiConversationItem) finalChats.push(aiConversationItem);
      finalChats.push(...mappedChats);
      // Ensure the default AI placeholder is present when no server AI conversation is available
      if (!aiConversationItem) finalChats.push(...defaultChatOptions);
      setChats(finalChats);
    } catch (err) {
      console.error('Failed to load conversations', err);
      // Fallback to default options on error
      setChats(defaultChatOptions);
    }
  };

  // Load messages for AI conversation when its id is known or when selected
  useEffect(() => {
    (async () => {
      try {
        const convId = aiConversationId || selectedChat;
        if (!convId) return;
        // Only fetch when the selected chat is the AI conv or when we discovered it on load
        if (convId && (selectedChat === convId || aiConversationId === convId)) {
          setAiLoading(true);
          try {
            // Fetch all pages of messages (page size 200) until empty
            const pageSize = 200;
            let page = 1;
            let all = [];
            while (true) {
              const chunk = await messagesService.getByConversation(convId, { page, limit: pageSize });
              if (!Array.isArray(chunk) || chunk.length === 0) break;
              all = all.concat(chunk);
              if (chunk.length < pageSize) break;
              page++;
            }
            if (all.length > 0) {
              try {
                const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
                const uid = currentUser?.id;
                const mapped = all.map(m => {
                  const sender = m.senderId || m.SenderId || m.sender || m.Sender || null;
                  const idVal = m.id || m.messageId || m._id || m.Id || m.MessageId || Date.now();
                  const contentVal = m.content || m.Content || m.body || m.Body || m.text || m.Text || '';
                  const sentAtVal = m.sentAt || m.SentAt || m.sentAtUtc || m.createdAt || m.CreatedAt || m.sentAtString || '';
                  let type = 'ai';
                  try {
                    if (uid && sender) {
                      const s = typeof sender === 'string' ? sender : (sender?.toString ? sender.toString() : '');
                      type = (s.toLowerCase() === (uid || '').toLowerCase()) ? 'user' : 'ai';
                    }
                  } catch { type = 'ai'; }
                  return {
                    id: idVal,
                    type,
                    content: contentVal,
                    timestamp: sentAtVal
                  };
                });
                setAiMessages(mapped);
              } catch (mapErr) {
                setAiMessages(all);
              }
            }
          } catch (err) {
            console.warn('Failed to load AI conversation messages', err);
          }
          setAiLoading(false);
        }
      } catch (err) {
        console.warn('AI messages effect failed', err);
      }
    })();
  }, [aiConversationId, selectedChat]);

  useEffect(() => {
    loadConversations();

    // Reload conversations when a user logs in (so personal AI conversation and history load immediately)
    const onUserLogin = (e) => {
      try {
        const user = e?.detail?.user || JSON.parse(localStorage.getItem('user') || 'null');
        const userId = user?.id ? String(user.id) : null;
        if (userId) {
          const per = localStorage.getItem(`aiConversationId_${userId}`) || null;
          try { setAiConversationId(per); } catch {}
        }
      } catch {}
      try { loadConversations(); } catch {}
    };
    window.addEventListener('user:login', onUserLogin);

    let removeMemberHandler = null;
    let removeTypingHandler = null;

    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) await signalRService.start(token);

        removeMemberHandler = signalRService.on('MemberAdded', (payload) => {
          if (!payload) return;
          // If current conversation is affected, show a brief notice
          if (payload.conversationId && payload.conversationId === selectedChat) {
            const name = payload.displayName || payload.userId;
            // lightweight UX: toast or alert
            try { window.dispatchEvent(new CustomEvent('chat:member-added', { detail: payload })); } catch {}
            console.log('MemberAdded', name, payload);
            // Reload conversations
            loadConversations();
          }
        });

        removeTypingHandler = signalRService.on('Typing', (payload) => {
          if (!payload) return;
          // Could show ephemeral typing indicator; for now log
          console.log('Typing event', payload);
        });
      } catch (err) {
        console.warn('SignalR init failed', err);
      }
    })();

    return () => {
      if (removeMemberHandler) removeMemberHandler();
      if (removeTypingHandler) removeTypingHandler();
      // Do not stop the global SignalR connection here; app-level can manage lifecycle
      window.removeEventListener('user:login', onUserLogin);
    };
  }, [selectedChat]);

  const handleChatSelect = (chatId) => {
    // If selecting the built-in AI chat, ensure the server has an AI conversation and persist its id
    (async () => {
      if (chatId === 'ai') {
        try {
          // ask backend to ensure/create the per-user AI conversation
          const token = localStorage.getItem('token');
          const headers = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
          const res = await fetch(`${getApiBaseUrl()}/ai/ensure`, { method: 'POST', headers });
            if (res.ok) {
            const body = await res.json();
            const convId = body?.ConversationId || body?.conversationId;
            if (convId) {
              try {
                const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
                const userId = currentUser?.id ? String(currentUser.id) : null;
                if (userId) localStorage.setItem(`aiConversationId_${userId}`, convId);
                else localStorage.setItem('aiConversationId', convId);
              } catch {}
              setAiConversationId(convId);
              // select the real conversation id so the UI opens it directly
              setSelectedChat(convId);
            } else {
              // fallback to placeholder behavior
              setSelectedChat('ai');
            }
          } else {
            console.warn('AI ensure returned', res.status);
            setSelectedChat('ai');
          }
        } catch (err) {
          console.warn('Failed to ensure AI conversation', err);
          setSelectedChat('ai');
        }
      } else {
        setSelectedChat(chatId);
      }
      setShowChatList(false);
    })();
  };

  const handleBackToList = () => {
    setShowChatList(true);
    setSelectedChat(null);
  };

  const handleNewChat = async () => {
    if (creatingChat) return; // prevent double-clicks
    const username = window.prompt('Enter the username to start a chat with:');
    if (!username) return;
    setCreatingChat(true);
    try {
      const results = await userService.searchUsers(username, 1, 5);
      const matched = Array.isArray(results)
        ? results.find(u => (u.userName || u.username || u.email || '').toLowerCase() === username.toLowerCase())
        : (results && (results.userName || results.username || results.email) ? results : null);
      if (!matched) {
        alert('User not found');
        return;
      }

      const otherUserId = matched.id || matched.userId || matched._id;
      const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
      const callerId = currentUser?.id;

      let conv = null;
      // Prefer atomic ensure-one-to-one endpoint when available
      try {
        conv = await conversationsService.ensureOneToOne({ otherUserId });
      } catch (e) {
        console.warn('ensureOneToOne failed, falling back', e);
        try {
          // fallback to existing create conversation API
          conv = await chatService.createConversationApi([otherUserId, callerId]);
        } catch (inner) {
          try {
            conv = await chatService.createConversation([otherUserId, callerId]);
          } catch (inner2) {
            console.error('Failed to create conversation with all endpoints', inner2);
            throw inner2;
          }
        }
      }

      const convId = conv?.id || conv?.conversationId || conv?._id || null;
      const displayName = matched.displayName || matched.fullName || matched.username || matched.userName || matched.email;
        const avatar = matched.avatarUrl || matched.avatar_url || matched.image || matched.avatar || '👤';

      if (convId) {
                const newChatItem = { id: convId, name: displayName, avatar, status: 'Vừa tạo' };
        setChats(prev => [newChatItem, ...prev.filter(c => c.id !== convId)]);
        setSelectedChat(convId);
        setShowChatList(false);
      } else {
        alert('Conversation created but no id returned — check backend response');
      }
    } catch (err) {
      console.error('New chat error', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to create conversation');
    } finally {
      setCreatingChat(false);
    }
  };

  const renderSelectedChat = () => {
    const chatId = selectedChat || (fullPage ? aiConversationId || 'ai' : null);
    // If the selected chat matches the saved AI conversation id, render the AI chat component
    if (aiConversationId && chatId && aiConversationId === chatId) {
      return <AIChat
        onClose={onClose}
        onBack={handleBackToList}
        conversationId={aiConversationId}
        messages={aiMessages}
        loading={aiLoading}
        onSendMessage={async (message, options = {}) => {
          try {
            // ensure the conversationId is forwarded to the backend
            const opts = { ...options, conversationId: aiConversationId };
            const res = await chatService.getAIResponse(message, opts);
            return res;
          } catch (err) {
            console.error('AI chat error (messenger)', err);
            return null;
          }
        }}
      />;
    }

    switch (chatId) {
      case 'ai':
        return <AIChat onClose={onClose} onBack={handleBackToList} messages={aiMessages} loading={aiLoading} onSendMessage={async (message, options = {}) => {
          try {
            const res = await chatService.getAIResponse(message, options);
            return res;
          } catch (err) {
            console.error('AI chat error (messenger)', err);
            return null;
          }
        }} />;
      case 'group':
        return <GroupChat onClose={onClose} onBack={handleBackToList} />;
      default:
        return <GroupChat onClose={onClose} onBack={handleBackToList} conversationId={selectedChat} />;
    }
  };

  // Shared layout for fullPage and embedded modes
  return (
    <div className={`chat-interface ${fullPage ? 'fullpage-mode' : ''}`}>
      {fullPage && <Header />}
      <div className="chat-messenger-shell">
        <div className="chat-messenger-left">
          <div className="chat-list-header">
            <h2>Trò chuyện</h2>
            <div className="chat-list-actions">
              {/* New chat button removed per UX request */}
              {!fullPage && <button className="close-btn" onClick={onClose}>×</button>}
            </div>
          </div>

          <div className="chat-list-container">
            {(
              (chats.length ? chats : defaultChatOptions)
                // Only show the AI conversation item (labelled 'Trò chuyện AI')
                .filter(c => (c && (c.name === 'Trò chuyện AI' || c.id === 'ai')))
                .map(chat => (
                  <button
                    key={chat.id}
                    className={`chat-list-item ${selectedChat === chat.id ? 'active' : ''}`}
                    onClick={() => handleChatSelect(chat.id)}
                  >
                    <span className="chat-avatar">{chat.avatar}</span>
                    <div className="chat-info">
                      <span className="chat-name">{chat.name}</span>
                      <span className="chat-status">{chat.status}</span>
                    </div>
                  </button>
                ))
            )}
          </div>
        </div>

        <div className={`chat-messenger-center expanded-center`}>
          <div className="chat-main">{renderSelectedChat()}</div>
        </div>
        {/* Info panel removed — chat center expands to full width by default */}
      </div>

      {showAddMember && (
        <AddMemberModal
          conversationId={selectedChat}
          onClose={() => setShowAddMember(false)}
          onAdded={async (user) => {
            // When a member is successfully added to a conversation, also ensure there's a
            // separate one-to-one conversation between the current user and that person.
            try {
              const otherUserId = user.id || user.userId || user._id;
              if (!otherUserId) {
                // fallback: just add a placeholder entry
                const displayName = user.displayName || user.fullName || user.username || user.userName || user.email;
                setChats(prev => [{ id: selectedChat, name: displayName, avatar: user.avatarUrl || user.avatar || '👤', status: 'Thêm mới' }, ...prev]);
                return;
              }

              // ensureOneToOne will try to find an existing 1:1 conv or create one.
              const conv = await conversationsService.ensureOneToOne({ otherUserId });
              const convId = conv?.id || conv?.conversationId || conv?._id || null;
              const displayName = user.displayName || user.fullName || user.username || user.userName || user.email;

              if (convId) {
                // Insert or move the conversation to the top of the list and open it.
                setChats(prev => [{ id: convId, name: displayName, avatar: user.avatarUrl || user.avatar || '👤', status: 'Vừa tạo' }, ...prev.filter(c => c.id !== convId)]);
                setSelectedChat(convId);
                setShowChatList(false);
              } else {
                // If backend returned no id, fall back to showing a placeholder in the current list.
                setChats(prev => [{ id: selectedChat, name: displayName, avatar: user.avatarUrl || user.avatar || '👤', status: 'Thêm mới' }, ...prev]);
              }
            } catch (err) {
              console.error('Error ensuring 1:1 conversation for added member', err);
              // Best-effort fallback: show a lightweight entry referencing the added user
              const displayName = user.displayName || user.fullName || user.username || user.userName || user.email;
              setChats(prev => [{ id: selectedChat, name: displayName, avatar: user.avatar || '👤', status: 'Thêm mới' }, ...prev]);
            }
          }}
        />
      )}
    </div>
  );
}